"use client";

import clsx from "clsx";

export interface Trade {
  id: string;
  orderId: string;
  asset: string;
  side: "buy" | "sell";
  size: number;
  price: number;
  fee: number;
  realizedPnl: number;
  timestamp: Date;
}

interface TradesTableProps {
  trades: Trade[];
  isLoading?: boolean;
}

export const TradesTable = ({ trades, isLoading = false }: TradesTableProps) => {
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-12 rounded bg-slate-700/50" />
        ))}
      </div>
    );
  }

  if (trades.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200/60 bg-white/5 p-8 text-center">
        <p className="text-sm text-slate-500">No trade history</p>
      </div>
    );
  }

  // Calculate summary stats
  const totalVolume = trades.reduce((sum, t) => sum + t.size, 0);
  const totalFees = trades.reduce((sum, t) => sum + t.fee, 0);
  const totalPnl = trades.reduce((sum, t) => sum + t.realizedPnl, 0);

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between rounded-lg bg-slate-800/50 px-4 py-2 text-xs">
        <div>
          <span className="text-slate-500">Trades: </span>
          <span className="text-white">{trades.length}</span>
        </div>
        <div>
          <span className="text-slate-500">Volume: </span>
          <span className="text-white">${totalVolume.toLocaleString()}</span>
        </div>
        <div>
          <span className="text-slate-500">Fees: </span>
          <span className="text-slate-300">${totalFees.toFixed(2)}</span>
        </div>
        <div>
          <span className="text-slate-500">Realized PnL: </span>
          <span className={totalPnl >= 0 ? "text-emerald-400" : "text-rose-400"}>
            {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-slate-200/60 bg-white/5">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-700 text-left text-slate-500">
              <th className="px-4 py-3 font-medium">Time</th>
              <th className="px-4 py-3 font-medium">Asset</th>
              <th className="px-4 py-3 font-medium">Side</th>
              <th className="px-4 py-3 font-medium text-right">Size</th>
              <th className="px-4 py-3 font-medium text-right">Price</th>
              <th className="px-4 py-3 font-medium text-right">Fee</th>
              <th className="px-4 py-3 font-medium text-right">Realized PnL</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {trades.map((trade) => (
              <tr key={trade.id} className="hover:bg-slate-800/30">
                <td className="whitespace-nowrap px-4 py-3 text-slate-400">
                  {new Date(trade.timestamp).toLocaleString()}
                </td>
                <td className="px-4 py-3 font-medium text-white">{trade.asset}</td>
                <td className="px-4 py-3">
                  <span
                    className={clsx(
                      "rounded px-2 py-0.5 font-semibold",
                      trade.side === "buy"
                        ? "bg-emerald-500/20 text-emerald-400"
                        : "bg-rose-500/20 text-rose-400"
                    )}
                  >
                    {trade.side.toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-white">
                  ${trade.size.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right text-white">
                  ${trade.price.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right text-slate-400">
                  ${trade.fee.toFixed(4)}
                </td>
                <td
                  className={clsx(
                    "px-4 py-3 text-right font-medium",
                    trade.realizedPnl >= 0 ? "text-emerald-400" : "text-rose-400"
                  )}
                >
                  {trade.realizedPnl !== 0
                    ? `${trade.realizedPnl >= 0 ? "+" : ""}$${trade.realizedPnl.toFixed(2)}`
                    : "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
